import * as React from "react"
import { createPortal } from "react-dom"
import { Check, ChevronDown } from "lucide-react"
import { cn } from "../../lib/utils"
import { inputBaseClass } from "./input-styles"

export interface SelectOption {
  value: string
  label: string
  /** Optional colour swatch shown before the label (e.g. a category colour). */
  dot?: string
}

interface SelectProps {
  value: string
  onChange: (value: string) => void
  options: SelectOption[]
  placeholder?: string
  disabled?: boolean
  className?: string
  containerClassName?: string
  "aria-label"?: string
  id?: string
}

const LIST_MAX_HEIGHT = 288
const GAP = 6
const OPTION_HEIGHT = 36

interface Position {
  left: number
  width: number
  top?: number
  bottom?: number
  maxHeight: number
}

/**
 * Custom dropdown (combobox + listbox) styled to match the app.
 * Focus stays on the trigger; arrow keys move the highlight, Enter/Space picks,
 * Escape/Tab closes, and typing jumps to the first matching option.
 * The list renders in a portal so cards can't clip or cover it.
 */
function Select({
  value,
  onChange,
  options,
  placeholder = "Select…",
  disabled,
  className,
  containerClassName,
  id,
  ...aria
}: SelectProps) {
  const reactId = React.useId()
  const listId = `${id ?? reactId}-listbox`
  const optionId = (index: number) => `${listId}-option-${index}`

  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const listRef = React.useRef<HTMLUListElement>(null)
  const typeahead = React.useRef({ text: "", timer: 0 })

  const [open, setOpen] = React.useState(false)
  const [activeIndex, setActiveIndex] = React.useState(-1)
  const [position, setPosition] = React.useState<Position | null>(null)

  const selectedIndex = options.findIndex((o) => o.value === value)
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null

  const updatePosition = React.useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom - GAP - 8
    const spaceAbove = rect.top - GAP - 8
    const width = Math.max(rect.width, 200)
    // Keep the list on-screen horizontally
    const left = Math.min(rect.left, window.innerWidth - width - 8)
    // Open upwards when the whole list doesn't fit below but there's more room above
    const listHeight = Math.min(LIST_MAX_HEIGHT, options.length * OPTION_HEIGHT + 10)
    const openUp = spaceBelow < listHeight && spaceAbove > spaceBelow
    setPosition(
      openUp
        ? { left, width, bottom: window.innerHeight - rect.top + GAP, maxHeight: Math.min(LIST_MAX_HEIGHT, spaceAbove) }
        : { left, width, top: rect.bottom + GAP, maxHeight: Math.min(LIST_MAX_HEIGHT, spaceBelow) }
    )
  }, [options.length])

  const openList = (index = selectedIndex >= 0 ? selectedIndex : 0) => {
    if (disabled) return
    updatePosition()
    setActiveIndex(index)
    setOpen(true)
  }

  const close = () => setOpen(false)

  const choose = (index: number) => {
    const option = options[index]
    if (option) onChange(option.value)
    close()
    triggerRef.current?.focus()
  }

  // Follow the trigger while open; close when clicking elsewhere
  React.useEffect(() => {
    if (!open) return
    const handlePointer = (e: MouseEvent) => {
      const target = e.target as Node
      if (!triggerRef.current?.contains(target) && !listRef.current?.contains(target)) close()
    }
    window.addEventListener("resize", updatePosition)
    window.addEventListener("scroll", updatePosition, true)
    document.addEventListener("mousedown", handlePointer)
    return () => {
      window.removeEventListener("resize", updatePosition)
      window.removeEventListener("scroll", updatePosition, true)
      document.removeEventListener("mousedown", handlePointer)
    }
  }, [open, updatePosition])

  // Keep the highlighted option in view
  React.useEffect(() => {
    if (!open || activeIndex < 0) return
    document.getElementById(optionId(activeIndex))?.scrollIntoView({ block: "nearest" })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeIndex])

  const jumpToTyped = (char: string) => {
    const state = typeahead.current
    window.clearTimeout(state.timer)
    state.text += char.toLowerCase()
    state.timer = window.setTimeout(() => (state.text = ""), 600)
    const start = open ? activeIndex : selectedIndex
    const ordered = [...options.slice(start + 1), ...options.slice(0, start + 1)]
    const match = ordered.find((o) => o.label.toLowerCase().startsWith(state.text))
      ?? ordered.find((o) => o.label.toLowerCase().startsWith(char.toLowerCase()))
    if (!match) return
    const index = options.indexOf(match)
    if (open) setActiveIndex(index)
    else onChange(match.value)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const last = options.length - 1
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        if (!open) openList()
        else setActiveIndex((i) => Math.min(last, i + 1))
        break
      case "ArrowUp":
        e.preventDefault()
        if (!open) openList()
        else setActiveIndex((i) => Math.max(0, i - 1))
        break
      case "Home":
        if (open) {
          e.preventDefault()
          setActiveIndex(0)
        }
        break
      case "End":
        if (open) {
          e.preventDefault()
          setActiveIndex(last)
        }
        break
      case "Enter":
      case " ":
        e.preventDefault()
        if (open) choose(activeIndex)
        else openList()
        break
      case "Escape":
        if (open) {
          // Don't let Escape also close a surrounding dialog
          e.preventDefault()
          e.nativeEvent.stopImmediatePropagation()
          close()
        }
        break
      case "Tab":
        if (open) close()
        break
      default:
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) jumpToTyped(e.key)
    }
  }

  return (
    <div className={cn("relative", containerClassName)}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        aria-label={aria["aria-label"]}
        disabled={disabled}
        onClick={() => (open ? close() : openList())}
        onKeyDown={handleKeyDown}
        className={cn(
          inputBaseClass,
          "cursor-pointer items-center gap-2 pr-9 text-left",
          open && "border-brand ring-3 ring-brand/20",
          className
        )}
      >
        {selected?.dot && <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: selected.dot }} />}
        <span className={cn("min-w-0 flex-1 truncate", !selected && "text-fg-subtle")}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown
          className={cn(
            "pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-fg-subtle transition-transform duration-150",
            open && "rotate-180 text-fg-muted"
          )}
        />
      </button>

      {open &&
        position &&
        createPortal(
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label={aria["aria-label"]}
            tabIndex={-1}
            style={{
              position: "fixed",
              left: position.left,
              width: position.width,
              top: position.top,
              bottom: position.bottom,
              maxHeight: position.maxHeight,
            }}
            className="z-[60] animate-pop-in overflow-y-auto overscroll-contain rounded-xl border border-line-strong bg-elevated p-1 shadow-pop outline-none"
            // Keep focus on the trigger when clicking inside the list
            onMouseDown={(e) => e.preventDefault()}
          >
            {options.map((option, index) => {
              const isSelected = option.value === value
              const isActive = index === activeIndex
              return (
                <li
                  key={option.value}
                  id={optionId(index)}
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => choose(index)}
                  className={cn(
                    "flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] transition-colors duration-75",
                    isActive ? "bg-white/[0.07] text-fg" : "text-fg-muted",
                    isSelected && "text-fg"
                  )}
                >
                  {option.dot && <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: option.dot }} />}
                  <span className="min-w-0 flex-1 truncate">{option.label}</span>
                  {isSelected && <Check className="size-3.5 shrink-0 text-brand" strokeWidth={2.5} />}
                </li>
              )
            })}
          </ul>,
          document.body
        )}
    </div>
  )
}

export { Select }
